/**
 * PR Polygon URL State
 *
 * Serializes and restores the shareable state of the PR Polygons table to and
 * from URL query parameters, mirroring the cluster map's
 * {@link ./parasiteRateUrlState} module. This lets a user copy the current URL
 * and send it to someone else so they open the table with the same dataset
 * version, failure-mode toggle, categorical filters, and survey-year range.
 */

import type { RangeValue } from "@/components/shared/TableFiltersSelector"

// ---------------------------------------------------------------------------
// Query parameter names
// ---------------------------------------------------------------------------

/**
 * Centralised names of every query parameter this module reads and writes. Kept
 * short to keep shared URLs manageable.
 */
export const POLYGON_URL_PARAM = {
  /** Dataset version string (e.g. "v1.1"). */
  version: "v",
  /** "1" when the validation-failures view is active; absent otherwise. */
  failureMode: "failures",
  /** Comma-separated selected biblio IDs. */
  biblioId: "biblio_id",
  /** Comma-separated selected ISO3 country codes. */
  iso3: "iso3",
  /** Comma-separated selected methods. */
  method: "method",
  /** Survey-year range lower bound (inclusive). */
  yearFrom: "yearFrom",
  /** Survey-year range upper bound (inclusive). */
  yearTo: "yearTo",
} as const

/**
 * The categorical filter fields that are routed through the URL, paired with
 * the query parameter each uses.
 */
export const CATEGORICAL_URL_PARAMS: readonly { field: string; param: string }[] = [
  { field: "biblio_id", param: POLYGON_URL_PARAM.biblioId },
  { field: "iso3", param: POLYGON_URL_PARAM.iso3 },
  { field: "method", param: POLYGON_URL_PARAM.method },
]

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/**
 * The complete set of shareable PR Polygons state. Every field is optional: an
 * absent field means the URL did not specify it and the component should keep
 * its own default. This is what {@link parsePolygonUrlState} returns.
 */
export interface PrPolygonUrlState {
  /** Dataset version string, or undefined if not specified. */
  version?: string
  /** Whether the validation-failures view is active, or undefined. */
  failureMode?: boolean
  /**
   * Selected categorical filters as a field → set-of-values map. Only fields
   * actually present in the URL appear; an empty map means none were specified.
   */
  categoricalFilters: Map<string, Set<string>>
  /** The survey-year range, or undefined when neither bound was specified. */
  yearRange?: RangeValue
}

// ---------------------------------------------------------------------------
// List (de)serialization
// ---------------------------------------------------------------------------

/** Separator used to join multi-value list parameters in the URL. */
const LIST_SEP = ","

/**
 * Serialize an array of values into a single comma-separated query value. Each
 * value is individually URL-encoded before joining so values containing the
 * separator survive the round-trip. {@link parseList} reverses this.
 *
 * @param values - The list of values to serialize.
 * @returns A comma-joined string of per-value-encoded segments.
 */
function serializeList(values: string[]): string {
  return values.map((value) => encodeURIComponent(value)).join(LIST_SEP)
}

/**
 * Split a comma-separated query value into a trimmed, non-empty string array,
 * decoding each segment produced by {@link serializeList}.
 *
 * @param raw - The raw parameter value, or null if the parameter is absent.
 * @returns An array of values, or undefined if the parameter was absent.
 */
function parseList(raw: string | null): string[] | undefined {
  if (raw == null) {
    return undefined
  }
  const values: string[] = raw
    .split(LIST_SEP)
    .map((value) => decodeURIComponent(value.trim()))
    .filter((value) => value.length > 0)
  return values.length > 0 ? values : undefined
}

/**
 * Parse a query value as a finite number.
 *
 * @param raw - The raw parameter value, or null if the parameter is absent.
 * @returns The parsed number, or undefined if absent or not a finite number.
 */
function parseNumber(raw: string | null): number | undefined {
  if (raw == null) {
    return undefined
  }
  const parsed: number = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

// ---------------------------------------------------------------------------
// Parsing (URL -> state)
// ---------------------------------------------------------------------------

/**
 * Read the full shareable PR Polygons state out of a URLSearchParams instance.
 *
 * @param params - The URLSearchParams to read from (e.g. from useSearchParams).
 * @returns A {@link PrPolygonUrlState} with only the fields present in the URL.
 */
export function parsePolygonUrlState(params: URLSearchParams): PrPolygonUrlState {
  const version: string | null = params.get(POLYGON_URL_PARAM.version)
  const failureRaw: string | null = params.get(POLYGON_URL_PARAM.failureMode)

  // Build the categorical filter map from whichever categorical params are
  // present. Absent params are simply skipped (no entry in the map).
  const categoricalFilters = new Map<string, Set<string>>()
  for (const { field, param } of CATEGORICAL_URL_PARAMS) {
    const values: string[] | undefined = parseList(params.get(param))
    if (values != null) {
      categoricalFilters.set(field, new Set(values))
    }
  }

  // The year range is present only when at least one bound parses.
  const yearFrom: number | undefined = parseNumber(params.get(POLYGON_URL_PARAM.yearFrom))
  const yearTo: number | undefined = parseNumber(params.get(POLYGON_URL_PARAM.yearTo))
  const yearRange: RangeValue | undefined =
    yearFrom != null || yearTo != null
      ? { from: yearFrom ?? null, to: yearTo ?? null }
      : undefined

  return {
    version: version ?? undefined,
    failureMode: failureRaw != null ? failureRaw === "1" : undefined,
    categoricalFilters,
    yearRange,
  }
}

// ---------------------------------------------------------------------------
// Serialization (state -> URL)
// ---------------------------------------------------------------------------

/**
 * The current, fully-resolved PR Polygons state to serialize. Unlike
 * {@link PrPolygonUrlState} the caller always has a concrete value for version,
 * failure mode, and the filter collections (which may be empty).
 */
export interface SerializablePolygonState {
  /** Dataset version string. */
  version: string
  /** Whether the validation-failures view is active. */
  failureMode: boolean
  /** Selected categorical filters as a field → set-of-values map. */
  categoricalFilters: Map<string, Set<string>>
  /** The survey-year range (either bound may be null when not narrowed). */
  yearRange: RangeValue | null
}

/**
 * Build a URLSearchParams that captures the given PR Polygons state. Categorical
 * filters are written only when they have selections; failure mode is written
 * only when true; year bounds are written only when set.
 *
 * @param state - The current, fully-resolved state to serialize.
 * @returns A URLSearchParams ready to attach to the current location.
 */
export function buildPolygonSearchParams(state: SerializablePolygonState): URLSearchParams {
  const params = new URLSearchParams()

  if (state.version) {
    params.set(POLYGON_URL_PARAM.version, state.version)
  }
  if (state.failureMode) {
    params.set(POLYGON_URL_PARAM.failureMode, "1")
  }

  // Write each categorical filter that has at least one selected value.
  for (const { field, param } of CATEGORICAL_URL_PARAMS) {
    const selected: Set<string> | undefined = state.categoricalFilters.get(field)
    if (selected != null && selected.size > 0) {
      params.set(param, serializeList(Array.from(selected)))
    }
  }

  if (state.yearRange != null) {
    if (state.yearRange.from != null) {
      params.set(POLYGON_URL_PARAM.yearFrom, String(state.yearRange.from))
    }
    if (state.yearRange.to != null) {
      params.set(POLYGON_URL_PARAM.yearTo, String(state.yearRange.to))
    }
  }

  return params
}
